// Printable route report (like Pilot's route report, without patient names): one row per case or pickup,
// in the order the driver will visit. Shown full screen in the app, then printed with the browser.
import { S, driverStops, officeOf, officeName, driverName, routeOf } from '../store.js';
import { esc, prettyDate, fmtTime, intervals, hoursText, dowOf } from '../util.js';
import { officeStatus, unreachable } from './common.js';
import { $ } from '../util.js';

function rowsFor(stops) {
  const rows = [];
  stops.forEach((s, i) => {
    const o = officeOf(s);
    // Open or closed when the driver gets there (or today's hours when there's no ETA yet).
    let open = '';
    if (o?.hours) {
      if (unreachable(s)) open = "Can't reach while open";
      else if (s.eta && s.status === 'pending') open = officeStatus(o, s.eta).label;
      else { const h = hoursText(intervals(o.hours, dowOf(S.date))); open = h === 'Closed' ? 'Closed all day' : `Hours ${h}`; }
    }
    const base = { n: i + 1, office: officeName(s), address: o?.address || s.address || '', route: routeOf(s), status: s.status, eta: s.status === 'pending' && s.eta ? fmtTime(s.eta) : '', open, closed: unreachable(s) || (s.status === 'pending' && !!s.eta && o?.hours && officeStatus(o, s.eta).s === 'closed') };
    const tri = new Set(s.tryins || []);
    for (const c of s.cases || []) {
      const info = s.caseInfo?.[c] || {};
      rows.push({ ...base, type: tri.has(c) ? 'Try-In' : 'Delivery', caseNo: c, pan: info.pan || '', ship: info.ship || '', pickupDate: '', notes: '' });
    }
    for (const p of s.pickups || []) {
      rows.push({ ...base, type: 'Pickup', caseNo: '', pan: '', ship: '', pickupDate: s.date, notes: [p.notes, p.ready ? `ready ${p.ready}` : ''].filter(Boolean).join(', ') });
    }
    if (!(s.cases || []).length && !(s.pickups || []).length) rows.push({ ...base, type: 'Stop', caseNo: '', pan: '', ship: '', pickupDate: '', notes: '' });
  });
  return rows;
}

const md = (d) => (d ? `${Number(d.slice(5, 7))}/${Number(d.slice(8, 10))}` : '');

/** Build the report. who: { email } for one driver's route, or { route } for a Pilot route. */
export function reportHtml(who) {
  let stops; let title;
  if (who.email) {
    stops = driverStops(who.email);
    title = `${driverName(who.email)}: route report`;
  } else {
    stops = S.stops.filter((s) => routeOf(s) === who.route)
      .sort((a, b) => (a.driverEmail || '').localeCompare(b.driverEmail || '') || (a.seq ?? 999) - (b.seq ?? 999));
    title = `${who.route} report`;
  }
  const rows = rowsFor(stops);
  const routes = [...new Set(stops.map(routeOf).filter(Boolean))].join(', ');
  const counts = ['Delivery', 'Try-In', 'Pickup'].map((t) => [t, rows.filter((r) => r.type === t).length]).filter(([, n]) => n)
    .map(([t, n]) => `${n} ${t === 'Delivery' ? (n === 1 ? 'delivery' : 'deliveries') : t === 'Try-In' ? (n === 1 ? 'try-in' : 'try-ins') : (n === 1 ? 'pickup' : 'pickups')}`).join(', ');
  const now = new Date().toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  return `<div class="rpt">
  <div class="rpt-bar noprint"><button class="btn primary" data-act="printNow">Print</button><button class="btn ghost" data-act="closeReport">Close</button></div>
  <h1>${esc(title)}</h1>
  <div class="rpt-sub">Watson Dental Laboratory · ${esc(prettyDate(S.date))}${routes ? ` · ${esc(routes)}` : ''} · ${stops.length} stop${stops.length === 1 ? '' : 's'}${counts ? `, ${esc(counts)}` : ''} · printed ${esc(now)}</div>
  ${rows.length ? `<table class="rpt-t"><thead><tr><th>Stop</th><th>Type</th><th>Office</th><th>Address</th><th>ETA</th><th>Open?</th><th>Case #</th><th>Pan #</th><th>Ship date</th><th>Pickup date</th><th>Pickup notes</th><th>Done</th></tr></thead><tbody>
  ${rows.map((r, i) => `<tr class="${i > 0 && rows[i - 1].n !== r.n ? 'newstop' : ''}"><td>${i === 0 || rows[i - 1].n !== r.n ? r.n : ''}</td><td>${esc(r.type)}</td><td>${esc(r.office)}</td><td>${esc(r.address)}</td>${i === 0 || rows[i - 1].n !== r.n ? `<td>${esc(r.eta)}</td><td class="${r.closed ? 'closed' : ''}">${esc(r.open)}</td>` : '<td></td><td></td>'}<td class="mono">${esc(r.caseNo)}</td><td class="mono">${esc(r.pan)}</td><td>${esc(md(r.ship))}</td><td>${esc(md(r.pickupDate))}</td><td>${esc(r.notes)}</td><td>${r.status === 'delivered' ? '✓' : r.status === 'skipped' ? '✗' : '<span class="box"></span>'}</td></tr>`).join('')}
  </tbody></table>` : '<p>No stops on this route.</p>'}
  <div class="rpt-foot">No patient information included. Match boxes by case # and pan #.</div>
</div>`;
}

// Self-contained styles for the report tab: always black on white, whatever the app's theme.
const REPORT_CSS = `
:root { color-scheme: light; }
* { box-sizing: border-box; }
body { margin: 0; background: #fff; color: #0a0a0a; font: 12px/1.35 Arial, sans-serif; }
.rpt { padding: 16px; }
.rpt h1 { font-size: 18px; margin: 0 0 2px; }
.rpt-sub { color: #525252; margin-bottom: 10px; }
.rpt-bar { display: flex; gap: 8px; margin-bottom: 12px; position: sticky; top: 0; background: #fff; padding: 6px 0; }
.btn { font: 600 14px Arial, sans-serif; border-radius: 999px; padding: 8px 18px; cursor: pointer; border: 1px solid #d4d4d4; background: #fff; color: #0a0a0a; }
.btn.primary { background: #0166cc; border-color: #0166cc; color: #fff; }
.rpt-t { width: 100%; border-collapse: collapse; }
.rpt-t th { text-align: left; font-size: 11px; color: #404040; border-bottom: 1.5px solid #0a0a0a; padding: 4px 6px; white-space: nowrap; }
.rpt-t td { border-bottom: 1px solid #e5e5e5; padding: 4px 6px; vertical-align: top; color: #0a0a0a; }
.rpt-t tr.newstop td { border-top: 1px solid #a3a3a3; }
.rpt-t .mono { font-family: ui-monospace, Menlo, Consolas, monospace; }
.rpt-t td.closed { font-weight: 700; }
.rpt-t .box { display: inline-block; width: 12px; height: 12px; border: 1.5px solid #0a0a0a; border-radius: 2px; }
.rpt-foot { margin-top: 10px; color: #737373; font-size: 11px; }
@media screen and (max-width: 700px) { .rpt { overflow-x: auto; } .rpt-t { min-width: 760px; } }
@media print { @page { size: landscape; margin: 0.4in; } .noprint { display: none !important; } .rpt { padding: 0; } .rpt-t tr { break-inside: avoid; } }`;

/** Opens the report in its own browser tab, so the app stays where it was. */
export function openReport(who) {
  const w = window.open('', '_blank');
  if (!w) return openReportInApp(who); // pop-up blocked: show it over the app instead
  const body = reportHtml(who)
    .replace('data-act="printNow"', 'onclick="window.print()"')
    .replace('data-act="closeReport"', 'onclick="window.close()"');
  const title = (body.match(/<h1>(.*?)<\/h1>/) || [])[1] || 'Route report';
  w.document.open();
  w.document.write(`<!doctype html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><title>${title}</title><style>${REPORT_CSS}</style></head><body>${body}</body></html>`);
  w.document.close();
  w.focus();
}

function openReportInApp(who) {
  let root = $('#printRoot');
  if (!root) { root = document.createElement('div'); root.id = 'printRoot'; document.body.appendChild(root); }
  root.innerHTML = reportHtml(who);
  document.body.classList.add('printing');
  window.scrollTo(0, 0);
}
export function closeReport() {
  const root = $('#printRoot'); if (root) root.innerHTML = '';
  document.body.classList.remove('printing');
}
